// @flow
import React, { Component } from 'react';
import styled, { css, keyframes } from 'react-emotion';
import RadioGroup from '@material-ui/core/RadioGroup';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import consts, {
  colors,
  configStructure,
  fonts,
  stepNames,
} from '../assets/scripts/constants';
import { orderByName } from '../assets/scripts/helpers';
import iconArrow from '../assets/images/icon_arrow.svg';
import iconDescription from '../assets/images/icon_description.png';
import iconLoader from '../assets/images/icon_loader.gif';
import A from './A';
import Slider from './Slider';
import Radio from './Radio';
import Select from './Select';
import Button from './Button';
import AddToCart from './AddToCart';

/**
 * Styles
 */

const textFieldCss = css`
  display: block;
  width: 50%;
  max-width: 24em;
  min-width: 16em;

  > div {
    width: 100%;
    margin-top: ${0.25 * consts.cell}em;
    margin-bottom: ${0.5 * consts.cell}em;
    background-color: ${colors.white};

    > input {
      padding-left: 0.4em;
      padding-right: 0.4em;
      border: 1px solid ${colors.gray};
    }
  }
`;

// Hacking to make animation fire every step change.
const stepChange = currentStep => keyframes`
  from {
    opacity: 0.${currentStep};
  }

  to {
    opacity: 1;
  }
`;

const Wrapper = styled('section')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${28 * consts.cell}em;
  padding-bottom: ${consts.cellEm};
  box-sizing: content-box;
  background-color: ${colors.whiteNear};
  animation: ${({ currentStep }) =>
    currentStep === 0 && `0.3s ease-in ${stepChange(currentStep)}`};
  animation: ${({ currentStep }) =>
    currentStep === 1 && `0.3s ease-in ${stepChange(currentStep)}`};
  animation: ${({ currentStep }) =>
    currentStep === 2 && `0.3s ease-in ${stepChange(currentStep)}`};
  animation: ${({ currentStep }) =>
    currentStep === 3 && `0.3s ease-in ${stepChange(currentStep)}`};

  @media (min-width: 860px) {
    width: ${22 * consts.cell}em;
  }

  @media (min-width: 940px) {
    width: ${26 * consts.cell}em;
  }
`;

const Head = styled('header')`
  display: flex;
  min-height: ${4 * consts.cell}em;
  padding: 1.9em ${1.5 * consts.cell}em;
  box-sizing: border-box;
  background-color: ${colors.grayLighter};

  > h1 {
    flex: 1 0 0%;
    margin: 0;
    font-family: ${fonts.primary};
    font-size: 2em;
    font-weight: 800;
    text-align: center;

    > span {
      color: ${colors.orange};
    }
  }
`;

const Config = styled('div')`
  padding: ${2 * consts.cell}em ${2 * consts.cell}em ${consts.cellEm};
  overflow-x: auto;
`;

const Description = styled('div')`
  padding-bottom: ${0.5 * consts.cell}em;
  &::before {
    content: '';
    display: inline-block;
    width: 1.4em;
    height: 1.4em;
    margin-right: 0.6em;
    background-size: 1.4em 1.4em;
    background-image: url('${iconDescription}');
  }

  > span {
    font-size: 1.6em;
    color: ${colors.grayDark};
  }
`;

const Ready = styled('div')`
  padding: ${consts.cellEm} 0;
  text-align: center;

  > img {
    width: 3.5em;
    height: 3em;
  }

  > h1 {
    font-family: ${fonts.primary};
    font-size: 2.4em;
    color: ${({ error }) => (error ? colors.red : colors.orange)};
  }

  > strong {
    display: block;
    margin-bottom: ${0.5 * consts.cell}em;
    font-size: 2em;
  }

  > p {
    max-width: 12em;
    margin: 0 auto ${consts.cellEm};
    font-size: 1.6em;
  }

  > div {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;

    > div {
      display: inline-block;
      margin-bottom: ${0.5 * consts.cell}em;
    }
  }
`;

/**
 * Component: ConfiguratorMain
 *
 * This is where configuration selections are made by the user.
 */
class ConfiguratorMain extends Component<
  {
    configData: { [string]: Object },
    configOptions: { [string]: Object },
    goToFinalPage: Function,
    handleConfigChange: Function,
  },
  {
    currentStep: number,
    currentConfigId: number,
  }
> {
  constructor() {
    super();
    this.configContainer = React.createRef();
  }

  state = {
    currentStep: 0,
    currentConfigId: 0,
  };

  componentDidUpdate(prevProps: Object) {
    const { configOptions: prevConfigOptions } = prevProps;
    const { configData, configOptions } = this.props;
    if (
      !Object.keys(prevConfigOptions).length &&
      Object.keys(configOptions).length
    ) {
      // This is the potential starting config id, if data is provided via url query.
      let sliderId = Object.keys(configData).length - 1;
      // If last slide opened with url parameter, go to AddToCart.
      sliderId = sliderId === 11 ? 12 : sliderId;
      if (sliderId > 0) {
        this.changeSliderTo(sliderId);
      }
    }
  }

  configContainer /* : Object */;

  /**
   * Filters and sorts terms for config option building.
   *
   * @returns {Object[]} Filtered and sorted terms.
   */
  filterSortTerms({
    filters,
    terms,
  }: {
    filters: Array<{ [string]: Array<string> }>,
    terms: Array<{ [string]: string }>,
  } = {}) {
    const { configData } = this.props;
    const dataValues = Object.keys(configData).map(
      key => configData[key].value
    );
    // Check which filters have all their terms activated.
    const activeFilters = filters.filter(filter =>
      filter.slugs.every(termSlug => dataValues.indexOf(termSlug) !== -1)
    );
    // If no filters activated, output all options. If any filters are
    // activated, filter options only by the first active filter.
    let filteredTerms = activeFilters.length
      ? terms.filter(
          term =>
            [activeFilters[0]]
              .map(filter => filter.terms)
              .reduce(
                (allTerms, termsToAdd) => [...allTerms, ...termsToAdd],
                []
              )
              .indexOf(term.slug) !== -1
        )
      : terms;
    // $FlowFixMe
    filteredTerms = filteredTerms.sort(orderByName);

    return filteredTerms;
  }

  /**
   * Builds a configuration option for the user to interact with.
   */
  buildConfig({
    title,
    slug,
    template,
    filteredTerms = [],
  }: {
    title: string,
    slug: string,
    template: string,
    filteredTerms: Array<Object>,
  } = {}) {
    const { configData, handleConfigChange } = this.props;

    switch (template) {
      case 'select': {
        return (
          <div>
            <Select
              label={title}
              name={slug}
              onChange={event => handleConfigChange({ event, title, slug })}
              value={(configData[slug] && configData[slug].value) || ''}
            >
              {filteredTerms.map(term => (
                <MenuItem
                  data-id={term.id}
                  data-description={term.description}
                  data-price={term.price}
                  data-time={term.lead_time}
                  key={term.slug}
                  value={term.slug}
                >
                  {term.name}
                </MenuItem>
              ))}
            </Select>
            {configData[slug] && configData[slug].description ? (
              <Description>
                <span>Huom! {configData[slug].description}</span>
              </Description>
            ) : null}
          </div>
        );
      }

      case 'radioDoor':
        return (
          <div>
            <RadioGroup
              aria-label={title}
              name={slug}
              value={configData[slug] && configData[slug].value}
              onChange={event => handleConfigChange({ event, title, slug })}
              style={{ justifyContent: 'space-between', flexDirection: 'row' }}
            >
              {filteredTerms.map(term => (
                <Radio
                  door={term.slug}
                  data-id={term.id}
                  data-description={term.description}
                  data-price={term.price}
                  data-time={term.lead_time}
                  key={term.slug}
                  label={term.name}
                  value={term.slug}
                />
              ))}
            </RadioGroup>
            {configData[slug] && configData[slug].description ? (
              <Description>
                <span>Huom! {configData[slug].description}</span>
              </Description>
            ) : null}
          </div>
        );

      case 'radioColor':
        return (
          <div>
            <RadioGroup
              aria-label={title}
              name={slug}
              value={configData[slug] && configData[slug].value}
              onChange={event => handleConfigChange({ event, title, slug })}
              style={{ justifyContent: 'space-between', flexDirection: 'row' }}
            >
              {filteredTerms.map(term => (
                <Radio
                  color={term.slug}
                  data-id={term.id}
                  data-description={term.description}
                  data-price={term.price}
                  data-time={term.lead_time}
                  key={term.slug}
                  label={term.name}
                  value={term.slug}
                />
              ))}
            </RadioGroup>
            {configData[slug] &&
            configData[slug].value === 'ovilehti_erikoisvari' ? (
              <TextField
                className={textFieldCss}
                onChange={event =>
                  handleConfigChange({ event, slug: 'custom_color' })
                }
                placeholder="värikoodi"
                value={configData.pa_pinta.custom_color}
              />
            ) : null}
            {configData[slug] && configData[slug].description ? (
              <Description>
                <span>
                  Huom! {`${configData[slug].description}`}
                  {configData[slug] &&
                  configData[slug].value === 'ovilehti_erikoisvari' ? (
                    <React.Fragment>
                      {', '}
                      <A
                        href="https://www.tikkurila.fi/ammattilaiset/varit/varikartat_sisamaalaukseen/symphony_2436_-varikartta"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        värikartta
                      </A>
                    </React.Fragment>
                  ) : (
                    ''
                  )}
                </span>
              </Description>
            ) : null}
          </div>
        );

      // case 'radio'
      default:
        return (
          <div>
            <RadioGroup
              aria-label={title}
              name={slug}
              value={configData[slug] && configData[slug].value}
              onChange={event => handleConfigChange({ event, title, slug })}
              style={{ display: 'block', marginTop: '-1.6em' }}
            >
              {filteredTerms.map(term => (
                <Radio
                  data-id={term.id}
                  data-description={term.description}
                  data-price={term.price}
                  data-time={term.lead_time}
                  key={term.slug}
                  label={term.name}
                  value={term.slug}
                />
              ))}
            </RadioGroup>
            {configData[slug] && configData[slug].description ? (
              <Description>
                <span>Huom! {configData[slug].description}</span>
              </Description>
            ) : null}
          </div>
        );
    }
  }

  changeSliderTo = (id: number) => {
    let currentStep;

    switch (true) {
      case id > 11:
        currentStep = 3;
        break;
      case id === 11:
        currentStep = 2;
        break;
      case id >= 6:
        currentStep = 1;
        break;
      default:
        ({ currentStep } = this.state);
        break;
    }

    this.setState({
      currentConfigId: id,
      currentStep,
    });
  };

  changeStepBy = (incr: number) => {
    this.setState({
      currentStep: this.state.currentStep + incr,
    });
  };

  render() {
    const {
      changeSliderTo,
      changeStepBy,
      setState,
      state: { currentConfigId, currentStep },
      props: { configData, configOptions, goToFinalPage, handleConfigChange },
    } = this;

    const forwardDisabled =
      !(currentStep === 0 && Object.keys(configData).length >= 7) &&
      !(currentStep === 1 && Object.keys(configData).length >= 12) &&
      !(currentStep === 2 && Object.keys(configData).length >= 13);

    const nextStepDisabled =
      !(currentStep === 0 && Object.keys(configData).length >= 6) &&
      !(currentStep === 1 && Object.keys(configData).length >= 11) &&
      !(currentStep === 2 && Object.keys(configData).length >= 12);

    return !configOptions.finalPage && Object.keys(configOptions).length ? (
      <Wrapper currentStep={currentStep}>
        <Head>
          <Button
            disabled={currentStep === 0}
            navigator
            onClick={() => changeStepBy(-1)}
          >
            <img
              style={{ width: '0.8em', height: '1.6em' }}
              src={iconArrow}
              alt=""
            />
          </Button>
          <h1>
            <span>{`${currentStep + 1}/4`}</span>{' '}
            {`Valitse ${stepNames[currentStep]}`}
          </h1>
          <Button
            disabled={forwardDisabled}
            navigator
            onClick={() => changeStepBy(1)}
          >
            <img
              style={{
                width: '0.8em',
                height: '1.6em',
                transform: 'rotate(180deg)',
              }}
              src={iconArrow}
              alt=""
            />
          </Button>
        </Head>
        <Config innerRef={this.configContainer}>
          {configStructure.map(part => {
            // We discard WP provided ids.
            const { step, id, slug, preselect, template, filters = [] } = part;

            if (step !== currentStep) {
              return null;
            }

            const configuration = configOptions[slug];
            if (configuration) {
              const { name: title, terms } = configuration;
              const filteredTerms = this.filterSortTerms({ filters, terms });
              const content = this.buildConfig({
                title,
                slug,
                template,
                filteredTerms,
              });

              // Try to preselect a value.
              let isPreselected = false;
              const preselects = [preselect, filteredTerms[0].slug];
              let defaultId = '';
              let defaultDescription = '';
              let defaultLabel = '';
              let defaultPrice = '';
              let defaultTime = '';
              let defaultValue = '';
              preselects.forEach(termSlug => {
                if (defaultValue === '' && termSlug) {
                  filteredTerms.some(term => {
                    if (term.slug === termSlug) {
                      defaultId = term.id;
                      defaultDescription = term.description;
                      defaultLabel = term.name;
                      defaultPrice = `${term.price}`;
                      defaultTime = `${term.lead_time}`;
                      defaultValue = term.slug;
                      return true;
                    }
                    return false;
                  });
                }
              });
              if (defaultLabel && defaultValue) {
                isPreselected = {
                  id: defaultId,
                  description: defaultDescription,
                  label: defaultLabel,
                  price: defaultPrice,
                  slug,
                  time: defaultTime,
                  title,
                  value: defaultValue,
                };
              }

              return (
                <Slider
                  disabled={id >= Object.keys(configData).length}
                  handleConfigChange={handleConfigChange}
                  handleClick={sliderId => changeSliderTo(sliderId)}
                  isHidden={isPreselected && filteredTerms.length === 1}
                  isOpen={id === currentConfigId}
                  isPreselected={isPreselected}
                  isSelected={configData[slug]}
                  key={id}
                  parent={this.configContainer}
                  sliderId={id}
                  title={title}
                >
                  {content}
                </Slider>
              );
            }
            return null;
          })}
        </Config>
        {stepNames[currentStep + 1] ? (
          <Button
            disabled={nextStepDisabled}
            large
            onClick={() => {
              let nextConfigId = currentStep === 0 ? 6 : 11;
              nextConfigId = currentStep === 2 ? 12 : nextConfigId;

              setState.bind(this)({
                currentConfigId: nextConfigId,
                currentStep: currentStep + 1,
              });
            }}
          >
            {`Seuraava: valitse ${stepNames[currentStep + 1]}`}
          </Button>
        ) : (
          <Ready>
            <strong>Tuotteesi on valmis!</strong>
            <p>Valitse haluamasi määrä ja lisää tuote ostoskoriin.</p>
            <Select
              name="quantity"
              onChange={event =>
                handleConfigChange({ event, slug: 'quantity' })
              }
              value={(configData.quantity && configData.quantity.value) || '1'}
            >
              {[...Array(10).keys()].map(num => {
                const number = `${num + 1}`;
                return (
                  <MenuItem key={number} value={number}>
                    {number}
                  </MenuItem>
                );
              })}
            </Select>
            <AddToCart configData={configData} goToFinalPage={goToFinalPage} />
          </Ready>
        )}
      </Wrapper>
    ) : (
      <Wrapper>
        <Head>
          {configOptions.finalPage ? (
            configOptions.finalPage.header
          ) : (
            <h1>
              <span>Ladataan</span> konfiguraattoria...
            </h1>
          )}
        </Head>
        <Config>
          <Config style={{ paddingTop: `${consts.cell}em` }}>
            {configOptions.finalPage ? (
              <Ready
                error={
                  configOptions.finalPage &&
                  configOptions.finalPage.type &&
                  configOptions.finalPage.type === 'error'
                }
              >
                {configOptions.finalPage.content}
              </Ready>
            ) : (
              <img
                className={css`
                  display: block;
                  max-width: 17.5em;
                  margin: 4em auto 0;
                `}
                src={iconLoader}
                alt=""
              />
            )}
          </Config>
        </Config>
      </Wrapper>
    );
  }
}

export default ConfiguratorMain;



// WEBPACK FOOTER //
// ./src/components/ConfiguratorMain.js